/**
 * GH-268 — TourProvider.
 *
 * Owns the tour state machine and exposes it via `<TourContext>` for
 * `useTour()` consumers. Wraps the app once at the root (mounted in app.tsx).
 *
 * Server sync:
 *   - Reads `useAuth().user.tourCompletedAt` to derive the initial state.
 *   - On `complete`/`skip`, PATCHes `/api/auth/me/tour-completed` (defined in Phase 2).
 *     If the endpoint isn't deployed yet, the request 404s — the local state
 *     transition still happens so the UI is never blocked.
 *
 * Auto-navigation:
 *   - When the active step's route differs from the current location, calls
 *     `navigate(step.route)`. The overlay handles waiting for the anchor to
 *     mount on the new route.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api-client'
import { useAuth } from '../../hooks/use-auth'
import { useOnboardingPackages } from '../packages/use-onboarding-packages'
import { TourContext, type TourContextValue, type TourState } from './use-tour'
import {
  TOUR_STEPS,
  resolveStepRoute,
  TEMPLATE_ID_PLACEHOLDER,
  type TourStep,
  type TourStepId,
} from './tour-step-config'

/** Mutation hook for PATCH /api/auth/me/tour-completed (endpoint owned by Phase 2). */
function useMarkTourCompleted() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.patch<{ ok: true }>('/api/auth/me/tour-completed'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
    // Tour completion is best-effort — never block UI on a 404 in dev.
    onError: () => {},
  })
}

interface TourProviderProps {
  children: React.ReactNode
}

export function TourProvider({ children }: TourProviderProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const markCompleted = useMarkTourCompleted()

  // Derive initial state from server: completed if `tourCompletedAt` is set, else idle.
  const initialState: TourState = user?.tourCompletedAt ? 'completed' : 'idle'
  const [state, setState] = useState<TourState>(initialState)
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null)
  // Visit ledger — push the LEAVING step's id whenever index changes via
  // next()/prev(). Reset on start()/replay()/skip(). Render layer uses this
  // to draw a persistent ✓ marker on each visited anchor for the rest of
  // the run, giving "this is what I already covered" feedback.
  const [visitedSteps, setVisitedSteps] = useState<TourStepId[]>([])

  // Sync state when auth resolves after first render.
  // We only react to the *first* observation of a non-null user — replay() and
  // local transitions own the state afterwards.
  const syncedRef = useRef(false)
  useEffect(() => {
    if (syncedRef.current) return
    if (!user) return
    syncedRef.current = true
    setState(user.tourCompletedAt ? 'completed' : 'idle')
  }, [user])

  // Resolve a templateId from seeded packages (the tour anchors a few steps
  // to a concrete `:templateId` route). Phase 4 uses this; tour gracefully
  // skips template-pinned steps if no template package exists.
  // Only fetch packages once the user is authenticated. Mounting this hook
  // on unauthenticated routes (/login, /share, /pub) caused 401 noise.
  const { data: packagesData } = useOnboardingPackages({ limit: 50 }, { enabled: Boolean(user) })
  const templateId = useMemo<string | null>(() => {
    const items = packagesData?.items ?? []
    const tpl = items.find((p) => (p as { isTemplate?: boolean }).isTemplate === true)
    return tpl?.id ?? null
  }, [packagesData])

  const currentStep: TourStep | null =
    currentStepIndex !== null && currentStepIndex >= 0 && currentStepIndex < TOUR_STEPS.length
      ? TOUR_STEPS[currentStepIndex]
      : null

  const isLastStep = currentStepIndex === TOUR_STEPS.length - 1

  // ── Auto-navigate when the active step lives on a different route ──────────
  useEffect(() => {
    if (state !== 'running' || !currentStep) return
    // Skip auto-nav for template-pinned routes when no template exists.
    if (currentStep.route.includes(TEMPLATE_ID_PLACEHOLDER) && !templateId) return
    const target = resolveStepRoute(currentStep.route, templateId)
    if (location.pathname !== target) {
      navigate(target)
    }
  }, [state, currentStep, templateId, location.pathname, navigate])

  // ── Actions ────────────────────────────────────────────────────────────────
  const start = useCallback(() => {
    setState('running')
    setCurrentStepIndex(0)
    setVisitedSteps([])
  }, [])

  const next = useCallback(() => {
    setCurrentStepIndex((idx) => {
      if (idx === null) return 0
      const leaving = TOUR_STEPS[idx]
      if (leaving) {
        setVisitedSteps((prevList) =>
          prevList.includes(leaving.id) ? prevList : [...prevList, leaving.id],
        )
      }
      const nextIdx = idx + 1
      if (nextIdx >= TOUR_STEPS.length) {
        setState('completed')
        markCompleted.mutate()
        return null
      }
      return nextIdx
    })
  }, [markCompleted])

  const prev = useCallback(() => {
    setCurrentStepIndex((idx) => {
      if (idx === null || idx <= 0) return idx ?? 0
      const leaving = TOUR_STEPS[idx]
      if (leaving) {
        setVisitedSteps((prevList) =>
          prevList.includes(leaving.id) ? prevList : [...prevList, leaving.id],
        )
      }
      return idx - 1
    })
  }, [])

  const skip = useCallback(() => {
    setState('completed')
    setCurrentStepIndex(null)
    setVisitedSteps([])
    markCompleted.mutate()
  }, [markCompleted])

  const replay = useCallback(() => {
    setState('running')
    setCurrentStepIndex(0)
    setVisitedSteps([])
  }, [])

  const value: TourContextValue = useMemo(
    () => ({
      state,
      currentStepIndex,
      currentStep,
      totalSteps: TOUR_STEPS.length,
      isLastStep,
      visitedSteps,
      start,
      next,
      prev,
      skip,
      replay,
    }),
    [state, currentStepIndex, currentStep, isLastStep, visitedSteps, start, next, prev, skip, replay],
  )

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}
